import { Node, Region, MapStore } from '../store';
import { WIDTHS } from 'lib/constants';
import { refreshFreehandAncestorsOfList } from 'lib/util';

class AppUI {

  setCanvas(canvas) {
    this.canvas = canvas;
    this.refreshOnVisibilityChange = false;
  }

  stopTheWorldError(opts={}) {
    const { canvas } = this;

    if (canvas.isViewOnly() && !opts.skipViewOnlyCheck) {
      return;
    }
    canvas.store.stopTheWorldError = true;
    canvas.appHeaderTag.update();
    canvas.tags['connection-error-banners'].update();
  }

  setViewOnly() {
    this.canvas.store.setViewOnly();
    this.canvas.appHeaderTag.update();
  }

  unsetViewOnly() {
    this.canvas.store.unsetViewOnly();
    this.canvas.appHeaderTag.update();
  }

  getMatrix() {
    return this.canvas.getMatrix();
  }

  isIntercomWindowOpen() {
    return Boolean($('.intercom-messenger-frame').length);
  }

  // filter the given list of nodes to only those whose top left is in the current viewport
  getElementsInViewport(elements) {
    const {top,bottom,left,right} = this.getVisibleRegion();
    const matches = [];
    for (const element of elements) {
      if (element instanceof Node) {
        const firstVisible = element.getFirstVisibleAncestorNode();
        const nodeX = firstVisible.absolutePosition.x || firstVisible.x;
        const nodeY = firstVisible.absolutePosition.y || firstVisible.y;
        if (nodeX > left && nodeX < right && nodeY > top && nodeY < bottom) {
          matches.push(element);
        }
      } else if (element instanceof Region) {
        if (element.left > left && element.left < right && element.top > top && element.top < bottom) {
          matches.push(element);
        }
      }
    }
    return matches;
  }

  elementIsInViewport(element,margin) {
    if (element instanceof Node) {
      return this.nodeIsInViewport(element, margin);
    } else if (element instanceof Region) {
      return this.boxIsInViewport(element, margin);
    }
  }

  nodeIsInViewport(node, margin) {
    margin = margin || { top: 100, bottom: 100, left: 100, right: 100};
    const {top,bottom,left,right} = this.getVisibleRegion();

    if (node.absolutePosition.x > left + margin.left &&
      node.absolutePosition.x + node.width < right - margin.right &&
      node.absolutePosition.y > top + margin.top &&
      node.absolutePosition.y + node.height < bottom - margin.bottom ) {
      return true;
    }
    return false;
  }

  boxIsInViewport(box,margin) {
    const {top,bottom,left,right} = this.getVisibleRegion();
    if (box.left > left + margin.left && box.left < right - margin.right && box.top > top + margin.top && box.top < bottom - margin.bottom) {
      return true;
    }
    return false;
  }

  // return true if half of these nodes are in view
  areNodesMostlyInView(nodes) {
    const margin = this.centerViewportMargin();
    const inView = [];
    for (const node of nodes) {
      if (this.elementIsInViewport(node, { top: 0, bottom: 0, left: 0, right: 0})) {
        inView.push(node);
      }
    }
    if (inView.length < Math.floor(nodes.length / 2)) {
      return false;
    }
    return true;
  }

  areElementsAllInView(elements) {
    const margin = this.centerViewportMargin();
    const inView = [];
    for (const element of elements) {
      if (!this.elementIsInViewport(element, { top: 0, bottom: 0, left: 0, right: 0})) {
        return false;
      }
    }
    return true;
  }

  centerViewportMargin() {
    const margin = {
      top: window.innerHeight / 4,
      bottom: window.innerHeight / 4,
      left: window.innerWidth / 4,
      right: window.innerWidth / 4,
    };
    if (this.canvas.sidebarStatus !== 'closed') {
      margin.right = margin.right + WIDTHS.SIDEBAR_WIDTH;
    }
    if (this.isIntercomWindowOpen()) {
      margin.right = margin.right + 400;
    }
    if (this.canvas.toolsSidebarTag && this.canvas.toolsSidebarTag.isOpen) {
      margin.left = margin.left + 360;
    }
    return margin;
  }

  // what x/y coords does the current viewport cover
  getVisibleRegion() {
    const {scale, tx, ty} = this.getMatrix();
    const windowWidth = $(window).width();
    const windowHeight = $(window).height();
    const top = (0 - ty) / scale;
    const bottom = (windowHeight - ty) / scale;
    const left = (0 - tx) / scale;
    const right = (windowWidth - tx) / scale;
    return {top,bottom,left,right};
  }


 // like centerContent, but only move the minimal amount in order to pan the content into view
  followContent({ nodes }, opts={}) {
    nodes = nodes.filter(n => !n.hidden && n.getTopLevelParent().isShownNodeType());
    if (nodes.length === 0) return;
    // find extents...
    let [et, el, eb, er] = this.canvas.getContentExtents({nodes});
    const {top,bottom,left,right} = this.getVisibleRegion();

    let xDist = 0;
    let yDist = 0;
    const BUFFER = 50;
    if (et < top - BUFFER) {
      yDist = et - top - BUFFER;
    } else if (eb > bottom + BUFFER) {
      yDist = eb - bottom + BUFFER;
    }

    if (el < left - BUFFER) {
      xDist = el - left - BUFFER;
    } else if (er > right + BUFFER) {
      xDist = er - right + BUFFER;
    }

    let newTop = top + yDist;
    let newBottom = bottom + yDist;
    let newLeft = left + xDist;
    let newRight = right + xDist;

    this.canvas.panZoomTo([newTop, newLeft, newBottom, newRight], opts.scale, opts);
  }

  contentFitsInViewport( nodes ) {
    let [et, el, eb, er] = this.canvas.getContentExtents({nodes});
    const {top,bottom,left,right} = this.getVisibleRegion();
    const contentWidth = eb - et;
    const contentHeight = er - el;
    const viewWidth = bottom - top;
    const viewHeight = right - left;
    if (contentWidth <= viewWidth && contentHeight <= viewHeight) {
      return true;
    }
    return false;
  }

  _visibilityVariants() {
    return {
      hidden: 'visibilitychange',
      webkitHidden: 'webkitvisibilitychange',
      mozHidden: 'mozvisibilitychange',
      msHidden: 'msvisibilitychange'
    };
  }

  initializeTabVisibilityEvents() {
    const keys = this._visibilityVariants();
    let event;
    let eventKey;
    for (const key in keys) {
      if (key in document) {
        event = keys[key];
        eventKey = key;
        break;
      }
    }

    document.addEventListener(event, () => {
      // if the tab becomes visible (document.hidden (or webkitHidden,mozHidden,msHidden) is false)
      // and we triggered a prompt for refresh, then auto refresh this browser tab
      if (!document[eventKey] && this.refreshOnVisibilityChange) {
        this.setViewOnly();
        this.canvas.operation.sender.handlers.alert({
          content: 'Plectica is updating, please stand by…',
        });
        window.location.reload();
        return;
      }
    });
  }
  // return true if the current browser tab is visible, otherwise false
  isWindowVisible() {
    const keys = this._visibilityVariants();
    for (const key in keys) {
      if (key in document) {
        return !document[key];
      }
    }
    // if browser doesn't support visibility api, assume window is visible
    return true;
  }

  setRefreshOnVisibilityChange(bool) {
    this.refreshOnVisibilityChange = bool;
  }

  zoomToCardIfZoomedOut(nodeTag) {
    if (this.canvas.getMatrix().scale <= zoomUI.ZOOM_THRESHOLD_0) {
      this.canvas.panzoomToTextCenter(nodeTag,0.8,{animate: true});
    }
  }

  zoomToLinkIfZoomedOut(linkTag) {
    if (this.canvas.getMatrix().scale <= zoomUI.ZOOM_THRESHOLD_0) {
      const xs = [linkTag.fromPoint.x,linkTag.toPoint.x];
      xs.sort((a,b) => a - b);
      const left = xs[0];
      const right = xs[1];
      const ys = [linkTag.fromPoint.y,linkTag.toPoint.y];
      ys.sort((a,b) => a - b);
      const top = ys[0];
      const bottom = ys[1];
      console.log('zoom to',[top,left,bottom,right]);
      this.canvas.panZoomTo([top,left,bottom,right],0.8,{animate: true});
    }
  }

  // given a list of selected elements, get all relationships and parts that are members
  getMemberRelationshipElements(selectedElements) {
    if (!selectedElements || !selectedElements.length) {
      return {selectedNodes: [], selectedLinks: []};
    }
    const links = this.canvas.store.getLinks();
    const sortedLinkIds = this.canvas.store.sortLinksByDepth(links.map(l => l.id));
    sortedLinkIds.reverse();
    const selectedLinks = [];
    let selectedNodes = [];
    // start w/ links connecting higher level nodes then links connecting deeper nodes
    // add all children of selected elements
    for (const element of selectedElements) {
      if (element.instance && element.instance instanceof Node) {
        const descendents = element.instance.descendants();
        selectedNodes = selectedNodes.concat(descendents.map(d => this.canvas.nodeTags[d.id])).filter(d => Boolean(d));
      }
    }
    for (const id of sortedLinkIds) {
      const link = this.canvas.store.getLink(id);
      const fromAncestors = link.fromNode() && link.fromNode().ancestors();
      const toAncestors = link.toNode() && link.toNode().ancestors();

      // find out if the fromnode and to node share an ancestor
      const commonParent = fromAncestors && fromAncestors.find(f => {
        return toAncestors.find(t => t.id == f.id);
      });
      if (commonParent) {
        const selectedAncestor = commonParent.getLineage().find(n => {
          return selectedElements.find(e => e.id == n.id) || selectedNodes.find(_n => _n.id == n.id);
        });
        if (selectedAncestor) {
          selectedLinks.push(link);
          const linkNode = link.linkNode();
          if (linkNode) {
            selectedNodes.push(linkNode);
          }
        }
      }
    }
    return {selectedNodes,selectedLinks};
  }

  positionSubMenuToLeft({ positionReferenceElement }) {
    const rect = positionReferenceElement.getBoundingClientRect();
    const { top: y, right: x } = rect;
    const { SIDEBAR_WIDTH, NODE_MENU_WIDTH, TAG_POPOVER_WIDTH } = WIDTHS;
    const rightCanvasEdge = canvas.sidebarStatus === 'closed' ? window.innerWidth : window.innerWidth - SIDEBAR_WIDTH;
    const positionSubToLeft = rightCanvasEdge - x < NODE_MENU_WIDTH + TAG_POPOVER_WIDTH;
    return positionSubToLeft;
  }


  // given a node's x,y coordinates in the map, get the equivalent coordinates in the viewport
  mapCoordsToViewportCoords({x,y}) {
    const {scale,tx,ty} = this.canvas.getMatrix();
    return {
      x: (x * scale) + tx,
      y: (y * scale) + ty
    };
  }

  receivePostMessage(e) {
    const { origin, data } = e;
    if (data === 'fitMapContents') {
      this.canvas.fitMapContents();
    }
  }

  viewportCoordsToMapCoords({ x,y }) {
    const { scale, tx, ty } = this.getMatrix();
    return {
      x: (x - tx) / scale,
      y: (y - ty) / scale,
    };
  }

  /*
    order elements by waypoint order, and fall back on / follow with canvas position order
  */
  sortElementsByWaypointsOrPosition({ elements }) {

    const elementIndexes = {};
    const presentation = this.canvas.store.getPresentation();
    if (presentation && presentation.getWaypoints().length) {
      for (const element of elements) {
        elementIndexes[element.id] = elements.length + 1;
        const index = presentation.getWaypointIndexOfElement({elementId: element.id});
        if (index > -1) {
          elementIndexes[element.id] = index;
        }
      }
    }

    return this.sortElementsByPosition({ elements, elementIndexes });
  }

  /*
    sort list of elements in place based on position in map - left to right, top to bottom
  */
  sortElementsByPosition({ elements, elementIndexes={} }) {
    const topLevelResults = {};
    const depth = {};
    const firstVisibleAncestor = {};
    const orderIndexSum = {};
    const actualOrderIndexes = {};

    for (const element of elements) {

      if (elementIndexes[element.id] === undefined || elementIndexes[element.id] === null) {
        elementIndexes[element.id] = elements.length + 1;
      }
      if (element instanceof Node) {
        const n = element;
        const lineage = n.getLineage();
        topLevelResults[n.id] = lineage[0];
        depth[n.id] = lineage.length;
        const firstVisibleNode = n.getFirstVisibleAncestorNode();
        firstVisibleAncestor[n.id] = firstVisibleNode;
        orderIndexSum[n.id] = 0;
        let currentDepth = 0;


        if (firstVisibleNode.parentId && firstVisibleNode.getGoverningLayoutName() !== 'layout-freehand') {
          for (const _n of lineage) {
            if (!actualOrderIndexes[_n.id]) {
              if (_n.getParent()) {
                // orderIndex param is relative and unpredictable, so get an actual integer that's consistent...
                actualOrderIndexes[_n.id] = _n.getParent().children().findIndex(c => c.id === _n.id) + 1;
              } else {
                actualOrderIndexes[_n.id] = 1;
              }
            }
            const actualOrderIndex = actualOrderIndexes[_n.id] || 1;
            orderIndexSum[n.id] += (actualOrderIndex / Math.pow(10,currentDepth));
            currentDepth++;
          }
        } else {
          topLevelResults[n.id] = firstVisibleNode;
        }
      } else if (element instanceof Region) {
        topLevelResults[element.id] = {
          absolutePosition: {
            x: element.left,
            y: element.top,
          }
        };
        orderIndexSum[element.id] = 0;
      }
    }

    // sort results from top left to bottom right of the canvas, and parent to child, and siblings by orderIndex
    elements.sort((a,b) =>
      1000000000 * (elementIndexes[a.id] - elementIndexes[b.id]) +
      10000 * (topLevelResults[a.id].absolutePosition.x - topLevelResults[b.id].absolutePosition.x) +
      1000 * (topLevelResults[a.id].absolutePosition.y - topLevelResults[b.id].absolutePosition.y) +
      orderIndexSum[a.id] - orderIndexSum[b.id]
    );
  }

  loadStore(dump) {
    this.canvas.store.load(dump);
  }

  cloneStore() {
    const cloned = new MapStore({ title: window.map_title, roles: window.map_roles, access: window.access, settings: window.map_settings });
    cloned.load(JSON.parse(this.canvas.store.dumpJSON()));
    return cloned;
  }

  freshen() {
    const { canvas } = this;
    const { store } = canvas;

    this.canvas.nodesTag.update();
    this.canvas.linksTag.update();
    this.canvas.regionsTag.update();
    this.canvas.drawingsTag.update();

    const nodes = store.topNodes();
    if (nodes && nodes.length) {
      for (const node of nodes) {
        const parent = node.getParent();
        if (parent && parent.parentId) {
          continue;
        }
        const nodeTag = canvas.nodeTags[node.id];
        if (nodeTag) {
          nodeTag.update({ forceUpdate: true });
          nodeTag.updateStyle();
          nodeTag.updateTopLevelNeighborsLite();
          nodeTag.refreshZoomStepNodeSizes();
          nodeTag.updateTitleDisplay(node.title);
          nodeTag.updateSummaryDisplay(node.summary);
          nodeTag.freshenEyeball();
          nodeTag.updatePerspectiveDots();
        }
      }
      refreshFreehandAncestorsOfList(Array.from(store.getNodes()), canvas, store);
    }

    for (const linkNode of store.getLinkNodes()) {
      if (linkNode) {
        const linkNodeTag = canvas.nodeTags[linkNode.id];
        if (linkNodeTag) linkNodeTag.update({ forceUpdate: true });
      }
    }

    for (const link of store.getLinks()) {
      const linkTag = canvas.linkTags[link.id];
      if (linkTag) linkTag.update({ forceUpdate: true });
    }

    if (canvas.perspectivesListingTag) canvas.perspectivesListingTag.update();
    if (canvas.waypointsTabTag) canvas.waypointsTabTag.update();
    if (canvas.attributesTabTag) canvas.attributesTabTag.update();
    if (canvas.commentsLayerTag) canvas.commentsLayerTag.update();
    if (canvas.commentsSidebarTag) canvas.commentsSidebarTag.update();
  }

  highlightElements(elements, color) {
    if (this.previousHighlightedElements) this.unhighlightElements(this.previousHighlightedElements);

    if (!elements) return;

    if (elements.linkIds) {
      for (const linkId of elements.linkIds) {
        const linkTag = this.canvas.linkTags[linkId];
        if (!linkTag) continue;
        linkTag.setLocalBorderHighlightColor(color);
      }
    }
    if (elements.nodeIds) {
      for (const nodeId of elements.nodeIds) {
        const nodeTag = this.canvas.nodeTags[nodeId];
        if (!nodeTag) continue;
        nodeTag.setLocalBorderHighlightColor(color);
        this.highlightCardBorders(nodeTag.instance);
      }
    }
    if (elements.regionIds) {
      for (const regionId of elements.regionIds) {
        const regionTag = this.canvas.regionTags[regionId];
        if (!regionTag) continue;
        regionTag.setLocalBorderHighlightColor(color);
      }
    }
    this.previousHighlightedElements = elements;
  }

  unhighlightElements(elements) {
    if (this.previousHighlightedElements && elements === undefined) {
      elements = this.previousHighlightedElements;
    }

    if (!elements) return;

    if (elements.linkIds) {
      for (const linkId of elements.linkIds) {
        const linkTag = this.canvas.linkTags[linkId];
        if (!linkTag) continue;
        linkTag.unsetLocalBorderHighlightColor();
      }
    }
    if (elements.nodeIds) {
      for (const nodeId of elements.nodeIds) {
        const nodeTag = this.canvas.nodeTags[nodeId];
        if (!nodeTag) continue;
        nodeTag.unsetLocalBorderHighlightColor();
        this.unhighlightCardBorders(nodeTag.instance);
      }
    }
    if (elements.regionIds) {
      for (const regionId of elements.regionIds) {
        const regionTag = this.canvas.regionTags[regionId];
        if (!regionTag) continue;
        regionTag.unsetLocalBorderHighlightColor();
      }
    }
  }

  highlightCardBorders(node) {
    if (!node || !this.canvas.nodeTags[node.id] || !this.canvas.nodeTags[node.id].refs.container) {
      return;
    }
    this.canvas.nodeTags[node.id].refs.container.classList.add('highlight-card-borders');
  }

  unhighlightCardBorders(node) {
    if (!node || !this.canvas.nodeTags[node.id] || !this.canvas.nodeTags[node.id].refs.container) {
      return;
    }
    this.canvas.nodeTags[node.id].refs.container.classList.remove('highlight-card-borders');
  }

  isMapOwner() {
    return window.user && window.user.id === this.canvas.people.owner.id;
  }
}


// singleton instance
const appUI = new AppUI();
window.appUI = appUI;
export { AppUI, appUI };



// WEBPACK FOOTER //
// ./starling/src/lib/app-ui.js